import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngxs/store';
import { ScreeningState } from '@app/store/screening/screening.state';
import { ScreeningService } from '@app/store/screening/screening.service';
import { ToastrService } from 'ngx-toastr';
import { EMPTY, catchError, map, mergeMap, tap } from 'rxjs';
import { Router } from '@angular/router';
import { IpsRequestsService } from '@app/ips/services/ips-requests.service';

@Component({
  selector: 'app-checklist',
  templateUrl: './checklist.component.html',
  styleUrls: ['./_checklist.component.sass', '../../screening.sass'],
})
export class CheckListComponent implements OnInit {
  @Input() private nextStep: any;
  @Output() completedChange = new EventEmitter<boolean>();

  questions: any[] = [];
  formQuestions = this._formBuilder.group({});
  formComment: FormGroup;
  idTrade: string;
  idOperation: string;
  idSubCategory: string;
  idDisabled = false;
  notEmpty = false;
  checklistForm: FormGroup;
  result: any;

  constructor(
    private _store: Store,
    private _screeningService: ScreeningService,
    private _requestsService: IpsRequestsService,
    private _formBuilder: FormBuilder,
    private _toastrService: ToastrService,
    private _router: Router
  ) {
    this.checklistForm = this._formBuilder.group({
      additionalInformation: [''],
    });
  }

  ngOnInit(): void {
    const { idTrade, idOperation, idSubCategory } = this._store.selectSnapshot(ScreeningState);
    this.idTrade = idTrade;
    this.idOperation = idOperation;
    this.idSubCategory = idSubCategory;
    this._buildForm();
  }

  private _buildForm() {
    this._screeningService
      .getQuestions(this.idOperation, this.idSubCategory, this.idTrade)
      .pipe(
        map((questions) => {
          this.questions = questions;

          questions.forEach((question) => {
            const formControlName = `question_${question.idQuestion}`;
            let radioButtonControl: AbstractControl;
            if (!question.editEnabled) {
              const defaultAnswer = question.answers.find((a) => a.defaultAnswer);
              radioButtonControl = this._formBuilder.control({
                value: defaultAnswer?.value || false,
                disabled: !question.editEnabled,
              });
            } else {
              radioButtonControl = this._formBuilder.control(null, [Validators.required]);
            }
            this.checklistForm.addControl(formControlName, radioButtonControl);
            const selectControlName = `${formControlName}_select`;
            const selectControl = this._formBuilder.control('');
            this.checklistForm.addControl(selectControlName, selectControl);

            radioButtonControl.valueChanges.subscribe((value) => {
              if (
                value === false &&
                question.customerAnswer === true &&
                !!question.divergenceReasons.length
              ) {
                selectControl.setValidators(Validators.required);
              } else {
                selectControl.patchValue(null);
                selectControl.clearValidators();
              }
              selectControl.updateValueAndValidity();
            });
            radioButtonControl.updateValueAndValidity();
          });
        }),
        catchError(() => {
          this._toastrService.error(
            'Não foi possível carregar as perguntas. Por favor, tente novamente.'
          );
          return EMPTY;
        })
      )
      .subscribe();

    this._requestsService.getAdditionalInfoBy(this.idTrade).subscribe((result) => {
      this.checklistForm.get('additionalInformation').patchValue(result.additionalInformation);
    });
  }

  onChangeComment() {
    if (!this.idDisabled) {
      this.notEmpty = true;
    }
  }

  onSubmitComment(action: string): void {
    const comment = this.checklistForm.get('additionalInformation');

    if (comment.value !== '') {
      this.idDisabled = true;
    }

    const payload: any = {
      idTrade: this.idTrade,
      additionalInformation: comment.value,
    };

    this._updateComment(payload).subscribe({
      next: () => {
        if (action === 'save') {
          this._toastrService.success('Comentário salvo com sucesso.');
        } else {
          this._toastrService.success('Comentário editado com sucesso.');
        }

        this.notEmpty = false;
      },
      error: () =>
        this._toastrService.error(
          'Não foi possível salvar o comentário. Por favor, tente novamente.'
        ),
    });
  }

  private _saveAnswers(idTrade: string, payload: any) {
    return this._screeningService.saveAnswers(idTrade, payload);
  }

  private _updateComment(payload: { idTrade: string; additionalInformation: string }) {
    return this._screeningService.updateAdditionalInformation(payload);
  }

  shouldShowDivergence(question: {
    idQuestion: string;
    customerAnswer: boolean;
    divergenceReasons: any[];
  }): boolean {
    const alliedAnswer: boolean = this.checklistForm.get('question_' + question.idQuestion)?.value;
    const customerAnswer: boolean = question.customerAnswer;
    const hasDivergenceReasons = !!question.divergenceReasons.length;

    return alliedAnswer === false && customerAnswer === true && hasDivergenceReasons;
  }

  onSubmitQuestions() {
    const requestPayLoad: { idTrade: string; checkList: { answers: any[] } } = {
      idTrade: this.idTrade,
      checkList: { answers: [] },
    };
    const formValue = this.checklistForm.getRawValue();
    requestPayLoad.checkList.answers = this.questions.map((question) => ({
      id: question.idQuestion,
      value: formValue['question_' + question.idQuestion],
      idDivergenceReason: formValue['question_' + question.idQuestion + '_select'] || null,
    }));

    const requests$ = this._saveAnswers(this.idTrade, requestPayLoad);

    const comment = this.checklistForm.get('additionalInformation');
    if (comment.value !== '') {
      requests$.pipe(
        mergeMap(() =>
          this._updateComment({ additionalInformation: comment.value, idTrade: this.idTrade })
        )
      );
    }

    requests$
      .pipe(
        mergeMap(() => this._screeningService.setStatusScreening(this.idTrade, 'checklist')),
        tap(() => {
          this.completedChange.emit(true);
          this._toastrService.success('Checklist enviado com sucesso.');
          this.nextStep();
        }),
        catchError((response: any) => {
          const errorMsg =
            response.errors[0] ??
            'Não foi possível enviar o Checklist. Por favor, tente novamente.';
          this._toastrService.error(errorMsg);
          return EMPTY;
        })
      )
      .subscribe();
  }

  resetScreening() {
    this._screeningService.resetScreening(this.idTrade).subscribe(() => {
      this._router.navigate(['ips', 'triagem', 'buscar']);
    });
  }
}
